import axios from 'axios';

const API_ORIGIN = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
// On production (Cloudflare Pages), use same-origin API proxy.
// On dev, use the VITE_API_BASE_URL env var pointing to the local backend.
const baseURL = import.meta.env.PROD ? '/api' : `${API_ORIGIN.replace(/\/$/, '')}/api`;

const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token (full account preferred, anonymous fallback)
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('anon_session_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const requestUrl = originalRequest.url || '';

    // Never try to refresh in response to an auth endpoint itself.
    // A failed login should show its real error, not "No refresh token".
    const isAuthRequest =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/register') ||
      requestUrl.includes('/auth/refresh') ||
      requestUrl.includes('/auth/anonymous-session');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      try {
        // IMPORTANT:
        // In production this must go through the same-origin Cloudflare
        // Pages proxy so the browser sends the refreshToken cookie.
        const refreshUrl = import.meta.env.PROD
          ? '/api/auth/refresh'
          : `${API_ORIGIN.replace(/\/$/, '')}/api/auth/refresh`;

        const { data } = await axios.post(
          refreshUrl,
          {},
          {
            withCredentials: true,
          }
        );

        localStorage.setItem('accessToken', data.accessToken);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;

        return apiClient(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
